import React, { useState } from "react";
import styles from "./StatutTimeline.module.scss";
import tools from "../../../../../helpers/tools";
import useStore from "../../../../../context/useStore";

const positionVisuals = {
  CARROSSERIE: { color: "#6f42c1", icon: "fa-tools" },
  MECANIQUE: { color: "#fd7e14", icon: "fa-cogs" },
  NETTOYAGE: { color: "#20c997", icon: "fa-broom" },
  EN_ANNONCE: { color: "#ffc107", icon: "fa-bullhorn" },
  VEHICULE_EN_RELAIS: { color: "#0dcaf0", icon: "fa-exchange-alt" },
};

const PositionTimeline = ({
  positionHistorique,
  onAddPosition,
  onResetPosition,
  vehicleStatut,
  vehiclePosition,
}) => {
  const [state] = useStore();
  const [selectedPosition, setSelectedPosition] = useState("");
  const [showSelector, setShowSelector] = useState(false);

  const positionOptions = (
    state?.constant?.items?.FLOTTE?.POSITIONS || []
  ).map((position) => ({
    id: position.id,
    name: position.name,
    ...positionVisuals[position.id],
  }));

  const isImmobilised =
    vehiclePosition !== null && vehiclePosition !== undefined;

  // La modification manuelle de position est désormais autorisée quel que
  // soit le statut du véhicule (LS-617). La réinitialisation automatique
  // conserve sa propre restriction côté API.
  const canAddPosition =
    vehicleStatut !== undefined && vehicleStatut !== null;

  const canResetPosition = (() => {
    const raw =
      state?.constant?.items?.FLOTTE?.STATUTS_POSITION_MODIFIABLE || [];

    const allowedIds = raw
      .map((s) => {
        if (s && typeof s === "object") return s.id;
        return s;
      })
      .filter((v) => v !== undefined && v !== null)
      .map((v) => `${v}`);
    if (vehicleStatut === undefined || vehicleStatut === null) return false;
    return allowedIds.includes(`${vehicleStatut + ""}`);
  })();
  const getPositionConfig = (position) => {
    return positionOptions.find((p) => p.id === position) || null;
  };

  const handleAddPosition = () => {
    if (selectedPosition && onAddPosition) {
      onAddPosition({ position: selectedPosition });
      setSelectedPosition("");
      setShowSelector(false);
    }
  };

  const handleCancelAdd = () => {
    setSelectedPosition("");
    setShowSelector(false);
  };

  const sortedDateDescPositionHistorique = [...(positionHistorique || [])].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );

  return (
    <div className={styles.timeline}>
      {!canAddPosition && (
        <p className=" mb-0">
          Impossible d'ajouter une position car le véhicule n'a pas de statut.
        </p>
      )}

      <div
        className="d-flex justify-content-end align-items-center mb-2"
        style={{ gap: 8 }}
      >
        {isImmobilised && (
          <span className="badge badge-warning">{vehiclePosition}</span>
        )}

        {onResetPosition && canResetPosition && isImmobilised && (
          <button
            type="button"
            className="btn btn-sm btn-default text-lowercase"
            onClick={() => onResetPosition()}
          >
            <i className="fas fa-undo mr-1"></i>
            Réinitialiser la position
          </button>
        )}
      </div>

      {onAddPosition && canAddPosition && (
        <div
          className={`${styles.timelineItem} ${styles.addStatutItem} ${
            (positionHistorique || []).length === 0 ? styles.last : ""
          }`}
        >
          <div className={styles.timelineDate}></div>

          <div className={styles.timelineConnector}>
            <div
              className={`${styles.timelineDot} ${styles.addStatutDot} border`}
            >
              <i className="fas fa-plus"></i>
            </div>
            {(showSelector || (positionHistorique || []).length > 0) && (
              <div className={styles.timelineLine}></div>
            )}
          </div>

          <div className={styles.timelineContent}>
            {!showSelector ? (
              <div className={styles.timelineHeader}>
                <button
                  className="btn btn-default mt-1 btn-sm mb-0 text-lowercase"
                  onClick={() => setShowSelector(true)}
                >
                  <i className="fas fa-plus mr-1"></i>
                  Ajouter une position
                </button>
              </div>
            ) : (
              <div className={styles.addStatutForm}>
                <div className={styles.timelineHeader}>
                  <div className="">
                    <select
                      className="form-control rounded-pill"
                      value={selectedPosition}
                      onChange={(e) => setSelectedPosition(e.target.value)}
                    >
                      <option value="">Sélectionner une position...</option>
                      {positionOptions.map((pos) => (
                        <option key={pos.id} value={pos.id}>
                          {pos.name}
                        </option>
                      ))}
                    </select>
                    <div className="d-flex" style={{ gap: "0.5rem" }}>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleAddPosition}
                        disabled={!selectedPosition}
                      >
                        <i className="fas fa-check mr-1"></i>
                        Valider
                      </button>
                      <button
                        className="btn btn-default btn-sm shadow-none border-0"
                        onClick={handleCancelAdd}
                      >
                        <i className="fas fa-times mr-1"></i>
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {sortedDateDescPositionHistorique.map((item, index) => {
        const cfg = getPositionConfig(item.position);
        return (
          <div
            key={index}
            className={`${styles.timelineItem} ${
              index === sortedDateDescPositionHistorique.length - 1
                ? styles.last
                : ""
            }`}
          >
            <div className={styles.timelineDate}>
              <small className="text-muted">
                {tools.formatDate(item.date)}
              </small>
            </div>

            <div className={styles.timelineConnector}>
              <div
                className={`${styles.timelineDot} border`}
                style={{ backgroundColor: cfg?.color || "#6c757d" }}
              >
                <i className={`fas ${cfg?.icon || "fa-map-marker-alt"}`}></i>
              </div>
              {index < sortedDateDescPositionHistorique.length - 1 && (
                <div className={styles.timelineLine}></div>
              )}
            </div>

            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <span className="badge badge-secondary">{item.position}</span>
              </div>

              {item.utilisateur && (
                <div className={styles.timelineUser}>
                  <i className="fas fa-user text-muted mr-1"></i>
                  <span className="text-muted">{item.utilisateur}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PositionTimeline;
